import { CommonModule } from '@angular/common';
import {
  AfterContentInit,
  Component,
  ContentChildren,
  Directive,
  Input,
  QueryList,
  TemplateRef,
} from '@angular/core';
import { AbstractControl, NgModel } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { getTimeZoneOffset } from '@osos/shared/legacy-common';
import { UserUiSettings } from '@osos/shared/legacy-core';
import { FieldPresenterModel } from '@osos/shared/types';
import { PropertyTranslatorPipe, TimeZoneDatePipe } from '../../pipes';
import { LocaleLanguageNamePipe } from '../locale-language-name/locale-language-name.pipe';
import { TypeofPipe } from '../typeof/typeof.pipe';

// TODO: This Message trans keys map should be used only for errors that we don't control them (i.e required, min, max),
// Other errors should send the trans key instead of `true` to drop them from this large list!

const ERROR_MESSAGE_TRANS_KEYS_MAP = {
  required: 'error.isRequired',
  minlength: 'error.minLength',
  maxlength: 'error.maxLength',
  minDate: 'error.MIN_DATE',
  maxDate: 'error.MAX_DATE',
  minDateTime: 'error.MIN_DATE_TIME',
  maxDateTime: 'error.MAX_DATE_TIME',
  min: 'error.MIN',
  max: 'error.MAX',
  minTime: 'error.MIN_TIME',
  maxTime: 'error.MAX_TIME',
  minMonth: 'error.MIN',
  maxMonth: 'error.MAX',
  uniqueness: 'error.namesUniqueness',
  unavailable: 'error.namesUniqueness',
  namesUniqueness: 'error.NormalizedNameIsNotUnique',
  normalizedPatternInvalid: 'error.normalize-name-pattern',
  viewUniqueness: 'error.VIEW_UNIQUENESS',
  minMaxRangeInvalid: 'error.MIN_MAX_RANGE_ERROR',
  categoryHasNoOptions: 'error.CATEGORY_SHOULD_HAVE_ONE_OPTION_AT_LEAST',
  invalidExpression: 'error.invalid-expression',
  invalidType: 'error.invalid-type',
  startOutScope: 'error.INVALID_RANGE_FIELD',
  endOutScope: 'error.INVALID_RANGE_FIELD',
  invalidRange: 'error.INVALID_RANGE_FIELD',
  pattern: 'error.pattern',
  urlInvalid: 'error.urlInvalid',
  valueIsAlreadyExist: 'error.VALUE_IS_ALREADY_EXIST',
  noTimeLinesExist: 'error.NO_TIME_LINES_EXIST',
  notAllRequiredFieldsSelected: 'error.NOT_ALL_REQUIRED_FIELDS_SELECTED',
  validatePhoneNumber: 'error.PHONE_NUMBER_NOT_VALID',
  atLeastOneFilter: 'error.atLeastOneFilter',
  duplicatedFilter: 'error.DUPLICATED_FILTER',
  hasNoEntities: 'error.hasNoEntities',
  hasNoQueries: 'error.hasNoQueries',
  allQueriesConsumed: 'error.allQueriesConsumed',
  hasNoViews: 'error.hasNoViews',
  permissionScope: 'message.AT_LEAST_ONE_SCOPE',
  permissionRequired: 'message.PERMISSION_REQUIRED',
  invalidView: 'error.INVALID_VIEW',
  invalidCron: 'error.INVALID_CRON_EXPRESSION',
  repeatableFields: 'error.THERE_ARE_NO_FIELDS_OR_ALL_FIELDS_ARE_REPEATABLE',
  invalidFields: 'error.INVALID_FIELDS',
  invalidValue: 'error.INVALID_VALUE',
};

export type ErrorMessages = Partial<typeof ERROR_MESSAGE_TRANS_KEYS_MAP>;

@Directive({
   
  selector: '[errorTemplate]',
  standalone: true,
})
 
export class FormControlErrorTemplate {
  /** The form field control error name */
  @Input('errorTemplate') error: string;

  constructor(public template: TemplateRef<any>) {}
}

@Component({
  selector: 'app-form-error',
  templateUrl: './form-error.component.html',
  styleUrls: ['./form-error.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    PropertyTranslatorPipe,
    TypeofPipe,
    LocaleLanguageNamePipe,
    TimeZoneDatePipe,
  ],
})
export class FormErrorComponent implements AfterContentInit {
  @Input() control: AbstractControl | NgModel;
  @Input() field: FieldPresenterModel;
  @Input() messages: ErrorMessages = {};

  protected defaultMessages = ERROR_MESSAGE_TRANS_KEYS_MAP;
  protected errorTemplates: Record<string, TemplateRef<any>>;

  @ContentChildren(FormControlErrorTemplate)
  private _errorTemplates: QueryList<FormControlErrorTemplate>;

  ngAfterContentInit(): void {
    this.errorTemplates = this._errorTemplates.reduce((acc, curr) => {
      acc[curr.error] = curr.template;
      return acc;
    }, {});
  }
  constructor(private _userUiSettings: UserUiSettings) {}
  get dateFormat(): string {
    return this._userUiSettings?.dateFormat ?? 'mediumDate';
  }
  get dateTimeFormat(): string {
    return this._userUiSettings?.dateTimeFormat ?? 'medium';
  }
  get timeFormat(): string {
    return this._userUiSettings?.timeFormat ?? 'shortTime';
  }
  get dateTimeZone(): string {
    return this._userUiSettings?.timeZone;
  }
  get timeZoneOffset(): string {
    return getTimeZoneOffset(this.dateTimeZone);
  }
}
